// src/services/patientDocument.js

// Base API URL - should match other services
const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY;

/**
 * Upload a new document for the authenticated patient
 * @param {string} token - JWT authentication token
 * @param {File} file - The document file to upload
 * @param {string} encryptedFileName - Encrypted filename
 * @param {string} encryptedMetadata - Encrypted metadata JSON string
 * @param {Object} encryptionMetadata - Encryption metadata (iv, salt, etc.)
 * @returns {Promise<Object>} - Upload response with document info and presigned URL
 */
export async function uploadDocument(token, file, encryptedFileName, encryptedMetadata, encryptionMetadata) {
  try {
    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('encrypted_file_name', encryptedFileName);
    formData.append('encrypted_metadata', encryptedMetadata);
    formData.append('encryption_metadata', JSON.stringify(encryptionMetadata));

    const response = await fetch(`${API_BASE_URL}/patients/protected/documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
        // Note: Don't set Content-Type for multipart/form-data - browser sets it with boundary
      },
      body: formData
    });

    if (!response.ok) {
      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload document');
      } else if (response.status === 403) {
        throw new Error('Document limit reached. Upgrade to upload more documents.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
}

/**
 * Get all documents for the authenticated patient
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Response with documents array, counts, and slots info
 */
export async function getMyDocuments(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/patients/protected/documents`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        // No patient profile found
        return {
          documents: [],
          total_count: 0,
          slots_used: 0,
          slots_available: 1
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
}

/**
 * Get a specific document by ID
 * @param {string} token - JWT authentication token
 * @param {string} documentId - Document UUID
 * @returns {Promise<Object>} - Document object with presigned URL
 */
export async function getDocument(token, documentId) {
  try {
    const response = await fetch(`${API_BASE_URL}/patients/protected/documents/${documentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Document not found');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to access this document');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching document:', error);
    throw error;
  }
}

/**
 * Update document metadata (filename and/or metadata)
 * @param {string} token - JWT authentication token
 * @param {string} documentId - Document UUID
 * @param {Object} updates - Object containing optional encrypted_file_name, encrypted_metadata, encryption_metadata
 * @returns {Promise<Object>} - Updated document object
 */
export async function updateDocument(token, documentId, updates) {
  try {
    const response = await fetch(`${API_BASE_URL}/patients/protected/documents/${documentId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Document not found');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to update this document');
      } else if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Invalid update data');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating document:', error);
    throw error;
  }
}

/**
 * Delete a document
 * @param {string} token - JWT authentication token
 * @param {string} documentId - Document UUID
 * @returns {Promise<void>}
 */
export async function deleteDocument(token, documentId) {
  try {
    const response = await fetch(`${API_BASE_URL}/patients/protected/documents/${documentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Document not found');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to delete this document');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // DELETE returns 204 No Content on success
    return;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
}

/**
 * Download a document using its presigned URL
 * @param {string} presignedUrl - Presigned URL from document object
 * @param {string} filename - Filename for the download
 * @returns {Promise<void>}
 */
export async function downloadDocument(presignedUrl, filename) {
  try {
    // Open in new tab for viewing
    const link = document.createElement('a');
    link.href = presignedUrl;
    link.target = '_blank';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
}

/**
 * Check if user has available document slots
 * @param {number} slotsUsed - Current slots used
 * @param {number} slotsAvailable - Total slots available
 * @returns {boolean} - True if user can upload more documents
 */
export function canUploadDocument(slotsUsed, slotsAvailable) {
  return slotsUsed < slotsAvailable;
}

/**
 * Get user-friendly file size string
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "2.5 MB")
 */
export function formatFileSize(bytes) {
  if (!bytes) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get file type icon based on file extension or mime type
 * @param {string} fileType - File MIME type
 * @param {string} fileName - File name with extension
 * @returns {string} - Icon name for lucide-vue-next
 */
export function getFileIcon(fileType, fileName) {
  const extension = fileName?.split('.').pop()?.toLowerCase() || '';
  
  // Check MIME type first
  if (fileType) {
    if (fileType.startsWith('image/')) return 'FileImage';
    if (fileType.includes('pdf')) return 'FileText';
    if (fileType.startsWith('video/')) return 'FileVideo';
    if (fileType.startsWith('audio/')) return 'FileAudio';
    if (fileType.includes('word') || fileType.includes('document')) return 'FileText';
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return 'FileSpreadsheet';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return 'FilePresentation';
    if (fileType.includes('zip') || fileType.includes('archive')) return 'FileArchive';
  }
  
  // Fallback to extension
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  const documentExts = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
  const spreadsheetExts = ['xls', 'xlsx', 'csv'];
  const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz'];
  
  if (imageExts.includes(extension)) return 'FileImage';
  if (documentExts.includes(extension)) return 'FileText';
  if (spreadsheetExts.includes(extension)) return 'FileSpreadsheet';
  if (archiveExts.includes(extension)) return 'FileArchive';
  
  return 'File'; // Default icon
}

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum file size in MB (default 10MB)
 * @returns {Object} - { valid: boolean, error: string }
 */
export function validateFile(file, maxSizeMB = 10) {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }
  
  // Check file size
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return { valid: false, error: `File size must be less than ${maxSizeMB}MB` };
  }
  
  // Check if file has a name
  if (!file.name || file.name.trim() === '') {
    return { valid: false, error: 'File must have a name' };
  }
  
  return { valid: true, error: null };
}